import { GetLinkedBusinessAccountListRO, SigninEmailRO, SigninUser } from '@/apis/types/stepinAuth';
import { GetUserInfoRO } from '@/apis/types/stepinContent';
import { clearUserData } from '@/model/dexie/userDexie';
import { PromiseNull } from '@/utilities/stateTool';
import { getTabSync } from '@/utilities/tabSync';
import Dexie, { type EntityTable } from 'dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import { mutate } from 'swr';

export const ListSort = {
	ADMIN: 'Manager',
	OWNER: 'Admin',
	USER: 'Main Personal',
};

type AuthorityList = keyof typeof ListSort;

export type UserData = {
	index: 0;
	id: string;
	stepinId: string;
	nickname: string;
	profileImageUrl: string;
	verifyStatus?: boolean;
	tournamentVerifyStatus?: boolean;
	email?: string;
	marketingTerms?: boolean;
	contentTerms?: boolean;
	allowLikeNotification?: boolean;
	allowCommentNotification?: boolean;
	allowFollowNotification?: boolean;
	allowBoostNotification?: boolean;
	nation?: string;
	/** @description [`COMMON`, `BUSINESS` */
	accountType?: string;
	useFlag?: boolean;
};

export type NewUserData = {
	id: string;
	stepinId: string;
	authority: AuthorityList;
	nickname: string;
	profileImageUrl?: string;
	verifyStatus?: boolean;
	tournamentVerifyStatus?: boolean;
	email?: string;
	marketingTerms?: boolean;
	contentTerms?: boolean;
	allowLikeNotification?: boolean;
	allowCommentNotification?: boolean;
	allowFollowNotification?: boolean;
	allowBoostNotification?: boolean;
	nation?: string;
	/** @description [`COMMON`, `BUSINESS` */
	accountType?: string;
	useFlag?: boolean;
};

const newDumiUserData = {
	accountType: undefined,
	allowBoostNotification: undefined,
	allowCommentNotification: undefined,
	allowFollowNotification: undefined,
	allowLikeNotification: undefined,
	authority: 'USER',
	contentTerms: undefined,
	email: undefined,
	id: '',
	marketingTerms: undefined,
	nation: undefined,
	nickname: '',
	profileImageUrl: undefined,
	stepinId: '',
	tournamentVerifyStatus: undefined,
	useFlag: undefined,
	verifyStatus: undefined,
} as NewUserData;

type OwnerData = {
	index: 0;
	id: string;
};

export const ownerDB = new Dexie('OwnerDB') as Dexie & {
	current: EntityTable<OwnerData, 'index'>;
	list: EntityTable<NewUserData, 'id'>;
};

/**
 * current로 포인트만 관리하고
 * 전체 유저는 list에 통합
 * index 되지 않는 것은 키에 등록하지 않음
 */
ownerDB.version(6).stores({
	current: 'index',
	list: 'id,stepinId,authority',
});

/** TODO: 여기서 비즈니스 데이터 저장함  */
export const saveBusinessDataToDexie = async (businessRO: GetLinkedBusinessAccountListRO) => {
	putBusinessBulkList(businessRO);
};

export const clearOwnerData = async () => {
	await ownerDB.current.clear();
	await ownerDB.list.clear();
};

export const getCurrentOwnerData = async () => {
	const user = await ownerDB.current.get(0);
	if (user && user.id) {
		// const data = await ownerDB.list.toArray()
		const data = await ownerDB.list.get({ id: user.id });
		// console.log('getCurrentOwnerData:', data)
		// return data.filter((item) => item.id === id)
		return data;
	}
	return;
};

export const getGeneralUser = async () => {
	return ownerDB.list.get({ authority: 'USER' });
};

export const getOwnerUser = async () => {
	return ownerDB.list.get({ authority: 'OWNER' });
};

export const getBusinessUser = async () => {
	const data = await ownerDB.list.toArray();
	return data.filter((item) => item.authority !== 'USER');
};

/** current User 를 user로 변경
 * user는 무조건 하나니까 가능함
 */
export const putOwnerUser = async () => {
	// const users = await ownerDB.list.where('authority').equalsIgnoreCase('USER').toArray()
	const user = await ownerDB.list.get({ authority: 'USER' });
	console.log('GET:', user);
	if (user) {
		return ownerDB.current.put({ id: user.id, index: 0 });
	}
};

/** 계정 전환 후 데이터 삭제 */
export const revokeAccessAdmin = async (id: string) => {
	// const users = await ownerDB.list.where('authority').equalsIgnoreCase('USER').toArray()
	const user = await ownerDB.list.get({ id });
	await putOwnerUser();
	if (user) {
		const result = await ownerDB.list.delete(user.id);
		return result;
	}
};

export const putOwnerId = async (id: string) => {
	console.log('[putOwnerId] 계정 전환 시작:', id);
	const user = await ownerDB.list.get({ id: id });
	console.log('[putOwnerId] 로컬 사용자 정보:', user?.stepinId, user?.authority);

	if (user) {
		// 비즈니스 계정으로 전환하는 경우, 상세 정보 조회
		if (user.authority !== 'USER') {
			console.log('[putOwnerId] 비즈니스 계정으로 전환 - 상세 정보 조회 중');
			try {
				// dynamic import to avoid circular dependency
				const { getBusinessUserInfo } = await import('@/legacy/apis/repository/content');
				const detailedUserInfo = await getBusinessUserInfo({ businessAccountId: id });
				console.log('[putOwnerId] 비즈니스 계정 상세 정보 조회 완료:', detailedUserInfo);
			} catch (error) {
				console.error('[putOwnerId] 비즈니스 계정 상세 정보 조회 실패:', error);
				// 실패해도 기본 정보로 계정 전환은 진행
			}
		}

		const result = await ownerDB.current.put({ id: user.id, index: 0 });
		console.log('[putOwnerId] 계정 전환 완료');

		// 탭 동기화: 계정 전환 알림
		const tabSync = getTabSync();
		tabSync.broadcast('ACCOUNT_SWITCH', { userId: id, user });

		return result;
	}

	console.warn('[putOwnerId] 사용자를 찾을 수 없음:', id);
};

export const putSigninEmailRO = async (loginRO: SigninEmailRO) => {
	const result = await ownerDB.list.put({ ...newDumiUserData, ...loginRO.user });

	// 탭 동기화: 로그인 알림
	const tabSync = getTabSync();
	tabSync.broadcast('LOGIN', { user: loginRO.user });

	return result;
};

export const putSigninUser = (signinUser: SigninUser & { authority?: string }) => {
	return putSigninEmailRO({ user: signinUser });

	// return ownerDB.list.put({ ...newDumiUserData, ...signinUser })
};

export const putGetUserInfoRO = async (userInfoRO: GetUserInfoRO) => {
	// const authority = userInfoRO.userInfo.user.accountType === 'COMMON'  'USER'  as AuthorityList
	const authority = userInfoRO.userInfo.user.accountType === 'COMMON' ? 'USER' : 'USER';

	const bs = {
		...newDumiUserData,
		...userInfoRO.userInfo.user,
		authority: authority as AuthorityList,
	};
	console.log('putGetUserInfoRO::', bs);
	return ownerDB.list.put(bs);
};

export const putBusinessBulkList = async (businessRO: GetLinkedBusinessAccountListRO) => {
	const bs = businessRO.linkedBusinessAccountList.map((item) => {
		if ('accountType' in item.user) throw 'accountType';

		const authority = item.business.authority;

		// spread 순서 중요: 기본값 → 실제 데이터 → authority 덮어쓰기
		return { ...newDumiUserData, ...item.user, authority: authority as AuthorityList };
	});
	console.log('[putBusinessBulkList] 비즈니스 계정 데이터:', bs);
	return ownerDB.list.bulkPut(bs);
};

export const getCurrentUserId = async () => {
	const user = await getCurrentOwnerData();
	if (user) {
		return user.id;
	}
	return;
};

/** Current user info */
export const useGetCurrentOwner = () => {
	return useLiveQuery(async () => {
		const data = await getCurrentOwnerData();
		if (data) {
			return data;
		}
		return PromiseNull;
	});
};

/** OWNER */
export const useGetOwnerOwner = () => {
	return useLiveQuery(async () => {
		const data = await getOwnerUser();
		if (data) {
			return data;
		}
		return PromiseNull;
	});
};

/** USER */
export const useGetGeneralUser = () => {
	return useLiveQuery(async () => {
		const data = await getGeneralUser();
		if (data) return data;
		return PromiseNull;
	});
};

/**  */
export const useGetAllUsers = () => {
	return useLiveQuery(async () => {
		const data = await ownerDB.list.toArray();
		if (data) return data;
		return PromiseNull;
	});
};

export const useGetBusinessUser = () => {
	return useLiveQuery(async () => {
		const data = await getBusinessUser();
		if (data) {
			return data;
		}
		return PromiseNull;
	});
};

const clearCache = () => mutate(() => true, undefined, { revalidate: false });

// ...로그아웃 시 캐시 지우기

/**  */
export const allDataClear = async () => {
	await clearOwnerData();
	await clearUserData();
	clearCache();

	// 탭 동기화: 강제 로그아웃 알림
	const tabSync = getTabSync();
	tabSync.broadcast('FORCE_LOGOUT');
};
